import React, {useState, useEffect} from "react";
import {useCart} from "../contexts/CartContext";
import {useNavigate} from "react-router-dom";
import axios from "axios";
import {useSiteOptions} from "../contexts/SiteOptionsContext";

const Checkout: React.FC = () => {
  const {state, dispatch} = useCart();
  const {ecommerceEnabled, loading: optionsLoading} = useSiteOptions();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [email, setEmail] = useState("");
  const [signedBookRequested, setSignedBookRequested] = useState(false);
  const [customMessage, setCustomMessage] = useState("");

  useEffect(() => {
    if (optionsLoading) return;
    if (!ecommerceEnabled) {
      dispatch({type: "CLEAR_CART"});
      navigate("/shop", {replace: true});
      return;
    }
    if (state.items.length === 0) {
      navigate("/shop");
    }
  }, [
    dispatch,
    ecommerceEnabled,
    navigate,
    optionsLoading,
    state.items.length,
  ]);

  const handleCheckout = async () => {
    if (!ecommerceEnabled) {
      alert("Store checkout is currently disabled. Use Amazon links instead.");
      navigate("/shop");
      return;
    }

    if (!email.trim()) {
      alert("Please enter your email address");
      return;
    }

    setLoading(true);
    try {
      // Create order and Stripe checkout session
      const {data} = await axios.post("/api/orders", {
        email: email.trim(),
        items: state.items.map((item) => ({
          book_id: item.id,
          quantity: item.quantity,
        })),
        signed_book_requested: signedBookRequested,
        custom_message: signedBookRequested ? customMessage.trim() : null,
        shipping_address: {
          name: "Customer", // This could be expanded to collect more shipping info
          address: "To be provided",
          city: "To be provided",
          state: "To be provided",
          zip: "To be provided",
        },
      });

      if (data && data.url) {
        window.location.href = data.url;
      } else {
        alert("Failed to get Stripe checkout URL.");
      }
    } catch (err: any) {
      console.error("Checkout error:", err);
      const errorMessage = err.response?.data?.error || "Something went wrong";
      alert(`Failed to start checkout: ${errorMessage}`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-2xl mx-auto py-12 px-4 min-h-[calc(100vh-200px)]">
      <h1 className="text-3xl font-bold text-gray-900 mb-8">Checkout</h1>

      <div className="bg-white rounded-lg shadow-lg p-6">
        <h2 className="text-xl font-semibold text-gray-900 mb-6">
          Order Summary
        </h2>

        <div className="space-y-4 mb-6">
          {state.items.map((item) => (
            <div key={item.id} className="flex justify-between items-center">
              <div className="flex items-center gap-4">
                <div className="w-12 h-16 bg-gray-200 rounded flex items-center justify-center">
                  {item.cover_url ? (
                    <img
                      src={item.cover_url}
                      alt={item.title}
                      className="w-full h-full object-cover rounded"
                    />
                  ) : (
                    <div className="text-center text-gray-500 text-xs">
                      <p>Cover</p>
                    </div>
                  )}
                </div>
                <div>
                  <h4 className="font-medium text-gray-900">{item.title}</h4>
                  <p className="text-gray-600 text-sm">Qty: {item.quantity}</p>
                  {item.status?.toLowerCase() === "preorder" && (
                    <p className="text-xs text-primary-800 mt-1">
                      This is a preorder
                      {item.release_date
                        ? ` · expected release ${new Date(
                            item.release_date
                          ).toLocaleDateString()}`
                        : ""}
                      . You will receive an email when your order ships.
                    </p>
                  )}
                </div>
              </div>
              <span className="font-medium">
                ${(item.price * item.quantity).toFixed(2)}
              </span>
            </div>
          ))}
        </div>

        <div className="border-t pt-4 mb-6">
          <div className="flex justify-between text-lg font-semibold">
            <span>Total:</span>
            <span>${state.total.toFixed(2)}</span>
          </div>
        </div>

        {/* Email Input */}
        <div className="mb-6">
          <label
            htmlFor="email"
            className="block text-sm font-medium text-gray-700 mb-2"
          >
            Email Address *
          </label>
          <input
            type="email"
            id="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="your@email.com"
            required
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
          />
          <p className="text-xs text-gray-500 mt-1">
            This email will be used for your order confirmation and will
            pre-fill the Stripe checkout form
          </p>
        </div>

        {/* Signed Book Request */}
        <div className="mb-6">
          <div className="flex items-start">
            <input
              type="checkbox"
              id="signed-book"
              checked={signedBookRequested}
              onChange={(e) => setSignedBookRequested(e.target.checked)}
              className="mt-1 h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
            />
            <label htmlFor="signed-book" className="ml-2 text-sm text-gray-700">
              <span className="font-medium">Request signed copy</span>
              <p className="text-xs text-gray-500 mt-1">
                Check this box if you would like your book(s) to be personally
                signed by Hallie Parker
              </p>
            </label>
          </div>
        </div>

        {/* Custom Message */}
        {signedBookRequested && (
          <div className="mb-6">
            <label
              htmlFor="custom-message"
              className="block text-sm font-medium text-gray-700 mb-2"
            >
              Custom Message (Optional)
            </label>
            <textarea
              id="custom-message"
              value={customMessage}
              onChange={(e) => setCustomMessage(e.target.value)}
              placeholder="e.g., 'Happy Birthday Sarah!', 'To my favorite reader', or leave blank for a general signature"
              maxLength={200}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 resize-none"
            />
            <p className="text-xs text-gray-500 mt-1">
              {customMessage.length}/200 characters. This message will be
              included with your signed book.
            </p>
          </div>
        )}

        <button
          onClick={handleCheckout}
          disabled={loading}
          className="btn btn-primary w-full"
        >
          {loading ? "Preparing Checkout..." : "Proceed to Secure Checkout"}
        </button>

        <p className="text-sm text-gray-500 text-center mt-4">
          You'll be redirected to Stripe's secure checkout page. Your email will
          be pre-filled.
        </p>
      </div>
    </div>
  );
};

export default Checkout;
